import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FormFieldSet } from "@progress/kendo-react-form";
import { hyperlinkOpenIcon, downloadIcon } from "@progress/kendo-svg-icons";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
  ContentHeaderActions,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import { ControlledComboBox } from "@excent-one/react/form/inputs";
import { Button } from "@progress/kendo-react-all";
import CreateWithTypeButton from "@phrmpc/shared/components/CreateWithTypeButton";
import { notification } from "@excent-one/react/notification";
import { searchIncidentRecords } from "@phrmpc/infrastructure/phrmpc/api/incident-records";
import { getAllIncidentStatuses } from "@phrmpc/infrastructure/phrmpc/api/incident-statuses";
import { getAllIncidentTypes } from "@phrmpc/infrastructure/phrmpc/api/incident-types";
import { createIncidentRecord } from "@phrmpc/infrastructure/phrmpc/api/incident-records";
import {
  type IncidentRecordSummaryDto,
  type IncidentStatusDto,
  type IncidentTypeDto,
} from "@phrmpc/infrastructure/phrmpc/models";
import { FilterLogic, FilterOperator } from "@phrmpc/shared";
import { DateTime } from "luxon";
import { searchMemberBasicInfo } from "@phrmpc/infrastructure/phrmpc";
import styles from "./IncidentSearchForm.module.scss";
import { useAuthContext } from "@excent-one/auth";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";

const defaultSearchValues: IncidentRecordSummaryDto = {
  statusId: undefined,
  incidentTypeId: undefined,
  reportedMemberId: undefined,
};

const buildFilter = (values: IncidentRecordSummaryDto) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }
  if (values.incidentTypeId != null) {
    filters.push({
      field: "incidentTypeId",
      operator: FilterOperator.Equals,
      value: values.incidentTypeId,
    });
  }
  if (values.reportedMemberId != null) {
    filters.push({
      field: "reportedMemberId",
      operator: FilterOperator.Equals,
      value: values.reportedMemberId,
    });
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};

const IncidentSearchForm = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [, { isInAnyRole }] = useAuthContext();

  const canCreate = isInAnyRole("Coordinator");

  const { control, getValues, reset } = useForm<IncidentRecordSummaryDto>({
    defaultValues: defaultSearchValues,
  });

  const [appliedFilters, setAppliedFilters] =
    useState<IncidentRecordSummaryDto>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  const { data: statusOptions } = useQuery({
    queryKey: ["incident-statuses"],
    queryFn: () => getAllIncidentStatuses(),
  });

  const { data: typeOptions } = useQuery({
    queryKey: ["incident-types"],
    queryFn: () => getAllIncidentTypes(),
  });

  const { data: memberOptions } = useQuery({
    queryKey: ["members-basic"],
    queryFn: async () => {
      const result = await searchMemberBasicInfo({
        payload: { pageNumber: 1, pageSize: 500 },
      });
      return result.items ?? [];
    },
  });

  const COLUMNS: DataGridColumnProps<IncidentRecordSummaryDto>[] = [
    {
      field: "incidentNumber",
      title: "Incident Number",
      width: "200px",
      locked: true,
    },
    {
      field: "createdDate",
      title: "Date Created",
      width: "150px",
      display: ({ value }: any) => (
        <span>
          {value instanceof Date
            ? DateTime.fromJSDate(value).toFormat("MM/dd/yyyy")
            : "-"}
        </span>
      ),
    },
    {
      field: "dateReported",
      title: "Date Reported",
      width: "150px",
      display: ({ value }: any) => (
        <span>
          {value instanceof Date
            ? DateTime.fromJSDate(value).toFormat("MM/dd/yyyy")
            : "-"}
        </span>
      ),
    },
    {
      field: "createdByName",
      title: "Created By",
      width: "400px",
      readOnly: true,
    },
    {
      field: "incidentTypeName",
      title: "Type of Incident",
      width: "200px",
      readOnly: true,
    },
    {
      field: "reportedMemberName",
      title: "Person to be Reported",
      width: "400px",
      display: ({ value }: any) =>
        value ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "var(--kendo-color-secondary, #73747a)" }}>
            - Empty -
          </span>
        ),
    },
    {
      field: "statusName",
      title: "Incident Status",
      width: "150px",
      readOnly: true,
    },
    {
      field: "cycleAge",
      title: "Cycle Age (days)",
      width: "150px",
      readOnly: true,
    },
  ];

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: ["incident-records", appliedFilters, skip, pageSize],
    queryFn: () =>
      searchIncidentRecords({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters) as any,
        },
      }),
  });

  const handleSearch = () => {
    resetPaging();
    setAppliedFilters(getValues());
  };

  const handleClear = () => {
    reset(defaultSearchValues);
    resetPaging();
    setAppliedFilters(defaultSearchValues);
  };

  const onExport = () => {
    notification.error("Export is not yet available.");
  };

  const handleCreate = async (item: IncidentTypeDto) => {
    if (item.id == null) return;
    setCreating(true);
    try {
      const created = await createIncidentRecord({
        payload: { incidentTypeId: item.id },
      });
      navigate({
        to: "/incident-management/$id/edit",
        params: { id: created.id! },
      });
    } catch (e) {
      console.error(e);
      notification.error("Unable to create incident record. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Incident Record" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          buttonProps={{
            search: { onClick: handleSearch },
            clear: { onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            {statusOptions && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Incident Status"
                data={statusOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
            {memberOptions && (
              <ControlledComboBox
                control={control}
                name="reportedMemberId"
                label="Person to be Reported"
                data={memberOptions}
                dataItemKey="id"
                textField="fieldName"
                wrapperProps={{ className: "basic-search-field" }}
              />
            )}
            {typeOptions && (
              <ControlledComboBox
                control={control}
                name="incidentTypeId"
                label="Type of Incident"
                data={typeOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
        <ContentHeaderActions>
          {canCreate && (
            <CreateWithTypeButton
              items={typeOptions ?? []}
              label="Create Incident Record"
              onSelect={handleCreate}
              disabled={creating}
            />
          )}
          <Button
            fillMode="outline"
            themeColor="base"
            svgIcon={downloadIcon}
            onClick={onExport}
          >
            Export
          </Button>
        </ContentHeaderActions>
      </ContentHeader>

      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            className={styles.noWrapGrid}
            data={pagedResult?.items ?? []}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            readOnly
            hideRowCommandToggle={false}
            commands={[
              {
                command: "view",
                text: "View",
                svgIcon: hyperlinkOpenIcon,
              },
            ]}
            onCommandClicked={{
              view: (e) => {
                if (e.dataItem == null || !e.dataItem.id) return;
                navigate({
                  to: "/incident-management/$id/display",
                  params: { id: e.dataItem.id },
                });
              },
            }}
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching incident records found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
          />
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default IncidentSearchForm;
